import type { SizeChartAttributeType, SizeChartResultType } from 'src/types/decorationSpecs';
import type { ConditionType, GenericSelectorProductType, ProductPropertyType } from 'src/types/product';

export type { GenericSelectorProductType, ProductPropertyType, ProductOptionType } from 'src/types/product';

/**
 * Resolves apparel body Width/Length data from a product's attribute model
 * (`GenericSelectorProductType`, loaded by `GenericSelector` into Redux).
 *
 * Two kinds of derivation:
 * - `buildSizeChartFromProduct` — used by the `getSizeChart` selector for the Size Chart table in the decoration specs panel.
 * - `resolveBodyWidthMm`/`resolveBodyLengthMm` — used by the `getBodyMeasurements` selector for auto-placement geometry calculations.
 *
 * Both are name-pattern-matched against specific candidate property names (`Width`, `Body Width`,
 * `Length (A)`, etc. — see `BODY_WIDTH_PATTERNS`/`BODY_LENGTH_PATTERNS`) rather than any generic
 * "numeric property that varies by size" heuristic — a product's attribute model can contain
 * plenty of other numeric, unit-bearing, size-varying properties that aren't body measurements
 * (e.g. per-size shipping weight), and this module needs to identify *specifically* Width/Length,
 * not "anything that looks size-chart-shaped".
 */

/** Whether `condition` is satisfied by `selections` — `selections` is whatever the current
 * `merchantVariables`-style selection map has for that attribute, plus (when resolving one size
 * chart cell) the specific size being resolved for `Size` itself. Returns `false` (not satisfied)
 * when the condition's attribute isn't present in `selections` at all — an unresolved condition
 * can never be treated as matched. */
function conditionIsSatisfied(condition: ConditionType, selections: Record<string, string | undefined>): boolean {
    const selectedValue = selections[condition.attribute];
    if (selectedValue === undefined) {
        return false;
    }

    const isListed = (condition.values ?? []).some((entry) => entry.value === selectedValue);
    return condition.operator === 'notIn' ? !isListed : isListed;
}

/**
 * Resolves one property's value for a given selection. `staticAssignment` (a property with one
 * fixed value regardless of selection — e.g. a "one size fits all" garment's `Width`) always wins
 * when present; otherwise the first assignment whose conditions **all** match `selections` wins.
 */
export function resolvePropertyValue(
    property: ProductPropertyType,
    selections: Record<string, string | undefined>,
): string | undefined {
    if (property.staticAssignment) {
        return property.staticAssignment.value;
    }

    const match = (property.assignments ?? []).find((assignment) =>
        (assignment.conditions ?? []).every((condition) => conditionIsSatisfied(condition, selections)),
    );
    return match?.result.value;
}

/** Every `Size` value this product declares, in declared order. Empty for a product with no
 * `Size` option at all — not necessarily "not apparel": a "one size fits all" garment may have no
 * `Size` option either, and still have a `Width`/`Length` worth showing (see
 * `buildSizeChartFromProduct`'s single-column fallback for that case). */
function getSizes(product: GenericSelectorProductType): string[] {
    const sizeOption = product.options?.find((option) => option.name === 'Size');
    return (sizeOption?.values ?? [])
        .map((entry) => entry.value)
        .filter((value): value is string => value !== undefined);
}

/** One name-matching rule: `pattern`/`excludes` are lowercase substrings checked against a
 * property's `name`; `multiplier` scales a resolved value (e.g. for "Half Width" needing to be
 * doubled to get a full body width). */
export type MeasurementPatternType = { pattern: string; excludes: string[]; multiplier: number };

/**
 * Priority-ordered candidate names for a garment's body width (attribute naming varies across
 * brands and vendors — `"Width (B)"`, `"Body Width"`, `"Half Width"`, etc.).
 */
export const BODY_WIDTH_PATTERNS: MeasurementPatternType[] = [
    { pattern: 'width (b)', excludes: [], multiplier: 1 },
    { pattern: 'body width', excludes: [], multiplier: 1 },
    { pattern: 'product width', excludes: [], multiplier: 1 },
    { pattern: 'chest width', excludes: [], multiplier: 1 },
    { pattern: 'bust width', excludes: [], multiplier: 1 },
    { pattern: 'waist width', excludes: [], multiplier: 1 },
    // 'decoration' excluded because a product property list can also contain "Decoration Location N Width"
    // (a print area dimension, not garment body width) which would otherwise match the generic substring.
    { pattern: 'width', excludes: ['half', 'decoration'], multiplier: 1 },
    { pattern: 'half body width', excludes: [], multiplier: 2 },
    { pattern: 'half width', excludes: [], multiplier: 2 },
    { pattern: 'chest', excludes: ['half'], multiplier: 1 },
];

/**
 * Priority-ordered candidate names for a garment's body/product length.
 */
export const BODY_LENGTH_PATTERNS: MeasurementPatternType[] = [
    { pattern: 'length (a)', excludes: [], multiplier: 1 },
    { pattern: 'body length', excludes: [], multiplier: 1 },
    { pattern: 'product length', excludes: [], multiplier: 1 },
    { pattern: 'inseam length', excludes: [], multiplier: 1 },
    { pattern: 'inseam', excludes: [], multiplier: 1 },
    // 'decoration' excluded for the same reason as BODY_WIDTH_PATTERNS' generic fallback — guards
    // against a future "Decoration Location N Length"-style property, even though today's known
    // decoration-area properties are Width/Height, not Length.
    { pattern: 'length', excludes: ['sleeve', 'decoration'], multiplier: 1 },
];

/** First property (in `patterns` priority order) whose `name` matches, or `undefined` if none do.
 * Pure name matching, no value resolution — used for the size chart table, which shows whatever a
 * matched property's data says as-is, including any cell that can't currently resolve. */
function findMatchingProperty(
    properties: ProductPropertyType[],
    patterns: readonly MeasurementPatternType[],
): ProductPropertyType | undefined {
    for (const { pattern, excludes } of patterns) {
        const match = properties.find((property) => {
            const nameLower = property.name.toLowerCase();
            return nameLower.includes(pattern) && !excludes.some((exclude) => nameLower.includes(exclude));
        });
        if (match) {
            return match;
        }
    }
    return undefined;
}

/**
 * Builds the Size Chart table's rows: one per matched Width/Length property, one column per
 * declared `Size` (or a single implicit column when the product has no `Size` option at all — a
 * "one size fits all" garment may still have a `Width`/`Length` worth showing, via
 * `staticAssignment`, with nothing to vary a column by).
 *
 * `selections` should be the current `merchantVariables`-style map for every attribute other than
 * `Size` — `Size` itself is overridden per column internally, since the table's whole point is
 * showing every size at once.
 *
 * Returns `null` when `product` is absent (e.g. this product is on the legacy/serialized
 * attribute-model path, which has no per-size structure to read at all — see the summary doc's
 * "Known gap" note) or neither Width nor Length has a matching property with any resolvable value.
 */
export function buildSizeChartFromProduct(
    product: GenericSelectorProductType | undefined | null,
    selections: Record<string, string | undefined>,
): SizeChartResultType | null {
    if (!product?.properties) {
        return null;
    }

    const sizes = getSizes(product);
    const columns = sizes.length > 0 ? sizes : [''];

    const matches = [
        findMatchingProperty(product.properties, BODY_WIDTH_PATTERNS),
        findMatchingProperty(product.properties, BODY_LENGTH_PATTERNS),
    ].filter((property): property is ProductPropertyType => !!property);

    const attributes: SizeChartAttributeType[] = matches
        .map((property) => {
            const values: Record<string, string> = {};
            columns.forEach((size) => {
                const value = resolvePropertyValue(property, { ...selections, Size: size });
                if (value !== undefined) {
                    values[size] = value;
                }
            });
            return { attribute: property.name, unit: property.unitOfMeasure ?? '', values };
        })
        .filter((attribute) => Object.keys(attribute.values).length > 0);

    return attributes.length > 0 ? { attributes } : null;
}

const NUMERIC_AND_UNIT_PATTERN = /^([\d.]+)\s*(in|inch|inches|cm|mm)?/i;

/** Converts `rawValue` (which may be a plain number or contain an embedded unit suffix like "425mm"
 * or "21 in") to millimeters using `unit` as fallback when no unit suffix is embedded in `rawValue`.
 * Unrecognized/absent units default to inches, matching US-centric product models. Exported for
 * auto-placement calculations, alongside `resolvePropertyValue`/`BODY_WIDTH_PATTERNS`/`BODY_LENGTH_PATTERNS`. */
export function toMm(rawValue: string, unit: string | undefined): number | undefined {
    const trimmed = String(rawValue).trim();
    const match = NUMERIC_AND_UNIT_PATTERN.exec(trimmed);
    if (!match) {
        return undefined;
    }

    const value = parseFloat(match[1]);
    if (Number.isNaN(value)) {
        return undefined;
    }

    const effectiveUnit = (match[2] || unit || '').toLowerCase();

    switch (effectiveUnit) {
        case 'cm':
            return value * 10;
        case 'mm':
            return value;
        case 'in':
        case 'inch':
        case 'inches':
            return value * 25.4;
        default:
            return value * 25.4;
    }
}

/** Parses one already-built `SizeChartAttributeType` cell into millimeters, for `decorationSpecs.tsx`'s
 * mm-equivalent display alongside the native value. */
export function getSizeAttributeMm(attribute: SizeChartAttributeType, size: string): number | undefined {
    const raw = attribute.values[size];
    if (raw === undefined) {
        return undefined;
    }
    return toMm(raw, attribute.unit);
}

// Resolves a single garment measurement in millimeters for `size`, trying `patterns` in priority
// order and, within each pattern, every property whose name matches it: a matching-named property
// with no usable value for this size does not stop the search; the next matching property (then the
// next pattern) is tried instead. This differs from `findMatchingProperty` (used for the display table),
// which returns the first name match.
function resolveMeasurementMm(
    product: GenericSelectorProductType | undefined | null,
    patterns: readonly MeasurementPatternType[],
    selections: Record<string, string | undefined>,
    size: string,
): number | undefined {
    if (!product?.properties) {
        return undefined;
    }

    for (const { pattern, excludes, multiplier } of patterns) {
        for (const property of product.properties) {
            const nameLower = property.name.toLowerCase();
            if (nameLower.includes(pattern) && !excludes.some((exclude) => nameLower.includes(exclude))) {
                const raw = resolvePropertyValue(property, { ...selections, Size: size });
                if (raw === undefined) {
                    continue;
                }
                const mm = toMm(raw, property.unitOfMeasure);
                if (mm !== undefined && mm > 0) {
                    return mm * multiplier;
                }
            }
        }
    }
    return undefined;
}

/** Body width in millimeters for `size`. `selections` should be the current merchantVariables-style
 * map for every attribute other than `Size`. Returns undefined when no candidate property has a
 * usable value for this size — including a "one size fits all" garment, whose `Width` (if
 * present) resolves via `staticAssignment` regardless of `size`. */
export function resolveBodyWidthMm(
    product: GenericSelectorProductType | undefined | null,
    selections: Record<string, string | undefined>,
    size: string,
): number | undefined {
    return resolveMeasurementMm(product, BODY_WIDTH_PATTERNS, selections, size);
}

// Same purpose as resolveBodyWidthMm, for body/product length.
export function resolveBodyLengthMm(
    product: GenericSelectorProductType | undefined | null,
    selections: Record<string, string | undefined>,
    size: string,
): number | undefined {
    return resolveMeasurementMm(product, BODY_LENGTH_PATTERNS, selections, size);
}
